import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
// import frozen-moment
import FrozenMoment from "frozen-moment";
import { Col } from "react-bootstrap";
import GYAxios from "../../../util/GYAxios";
import { useSolarCalc } from "../../../util/calculations";
import MaterialTable from "material-table";
import summarytable from "./summarystyle.module.css";
import { isITUser } from "../../../util/roles";
import { useUser } from "../../../util/user";
import { isNaN, float_format } from "../../../util/formatter";
import { useUserProps } from "../../../util/userDBContext";

const MonthlyLossesRPRReport = () => {
    const width = 150;
    const icreport_cols = useMemo(() => [
        { title: "Company", field: "company", width },
        { title: "Site Name", field: "site_name", width },
        { title: "Country", field: "country", cellStyle: { display: "none" }, headerStyle: { display: "none" }, width },
        { title: "SF Project ID", field: "SF_project_id", width },
        { title: "COD", field: "cod", width },
        { title: "Capacity (kWh)", field: "power", width },
        { title: "Contract Scheme", field: "contract_id", width },
        { title: "Actual Production (kWh)", field: "actual_prod", width },
        { title: "Actual Irradiation (kWh/m\xB2)", field: "actual_irr", width },
        { title: "Module Temp (°C)", field: "module_temp", width },
        { title: "TA Irradiation (kWh/m\xB2)", field: "m_TA_irr", width },
        { title: "TA Production (kWh)", field: "m_TA_prod", width },
        { title: "TA loss (kWh)", field: "TA_loss", width },
        { title: "GA Irradiation (kWh/m\xB2)", field: "m_GA_irr", width },
        { title: "GA Production (kWh)", field: "m_GA_prod", width },
        { title: "GA loss (kWh)", field: "GA_loss", width },
        { title: "TPA Irradiation (kWh/m\xB2)", field: "m_TPA_irr", width },
        { title: "TPA Production (kWh)", field: "m_TPA_prod", width },
        { title: "TPA loss (kWh)", field: "TPA_loss", width },
        { title: "CA Irradiation (kWh/m\xB2)", field: "m_CA_irr", width },
        { title: "CA Production (kWh)", field: "m_CA_prod", width },
        { title: "CA loss (kWh)", field: "CA_loss", width },
        { title: "ZE Irradiation (kWh/m\xB2)", field: "m_ZE_irr", width },
        { title: "ZE Production (kWh)", field: "m_ZE_prod", width },
        { title: "ZE loss (kWh)", field: "ZE_loss", width },
        // { title: "Overlap Irradiation (kWh/m\xB2)", field: "overlap_irr", width },
        // { title: "Overlap Production (kWh)", field: "overlap_prod", width },
        /* { title: "Overlap loss", field: "overlap_loss", width }, */
        { title: "RPR (%)", field: "RPR", width },
        { title: "DEO (kWh)", field: "DEO", width },
        { title: "Unused (kWh)", field: "unused", width },
        { title: "Curtailment (kWh)", field: "curtailment", width }
    ], []);

    const user = useUser();
    const roles = user.roles;

    const [month_data, setMonthData] = useState([]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // check if today is the second day of the month or after the second day of the month
    const isSecondDay = moment().date() >= 2;
    const dateState = isSecondDay ? new Date(moment().subtract(1, "months").toDate()) : new Date(moment().subtract(2, "months").toDate());
    const [startDate, setStartDate] = useState(dateState);
    const endDate = isITUser(roles) ? new Date() : dateState;
    const { loggedCountry } = useUserProps();
    const [selectedCountry, setSelectedCountry] = useState(loggedCountry);

    const solarCalc = useSolarCalc();

    async function fetch_m_calc(uri) {
        try {
            const response = await GYAxios.get(uri);
            if (response.status === 200) {
                const data = response.data;
                if (data && data.length > 0) {
                    setMonthData(data);
                }
            }
        } catch (error) {
            console.log("error", error);
            return [];
        }
    }

    // async function fetch_d_calc(uri) {
    //     try {
    //         const response = await GYAxios.get(uri);
    //         if (response.status === 200) {
    //             const data = response.data;
    //             if (data && data.length > 0) {
    //                 setDailyData(data);
    //             }
    //         }
    //     } catch (error) {
    //         console.log("error", error);
    //         return [];
    //     }
    // }

    // async function fetch_o_calc(uri) {
    //     try {
    //         const response = await GYAxios.get(uri);
    //         if (response.status === 200) {
    //             const data = response.data;
    //             if (data && data.length > 0) {
    //                 setOverlapData(data);
    //             }
    //         }
    //     } catch (error) {
    //         console.log("error", error);
    //         return [];
    //     }
    // }

    function process_m_d_data(month_data) {
        if(month_data?.length) {
            month_data.forEach((p) => {
                /**
                 * Calculating for previous month report
                 * Step 1: get (daily prod during 100) and (daily irr during 100)
                 * Step 2: calculate (daily RPR prod) = (daily prod during 100) - (daily GA prod) - (daily TPA prod) - (daily CA prod) + (daily overlap prod) >= 0
                 *                   (daily RPR irr) = (daily irr during 100) - (daily GA irr) - (daily TPA irr) - (daily CA irr) + (daily overlap irr) >= 0
                 * Step 3: if (daily irradiation correction - aka log daily reinsert or daily prod correction exists for that day - aka log irr replacement) then (daily RPR prod) = (daily RPR irr) = 0
                 * Step 4: calculate (Daily RPR) = (daily RPR prod)/ (daily RPR irr) / Capacity)
                 * Step 5: if (daily RPR < Brut PR OR Daily RPR > 100%) then (daily RPR prod) = (daily RPR irr) = 0
                 * Step 6: calculate (monthly RPR prod) = sum(daily RPR prod) 
                 *                   (monthly RPR irr) = sum(daily RPR irr)
                 * Step 7: calculate (monthly RPR) = (monthly RPR prod)/ (monthly RPR irr) / Capacity)
                */

                //RPR / TheoPR = Monthly Prod during Percentage control 100% / Monthly Irradiance during Percentage control 100% / Capacity x 100 --> monthly = sum(daily)
                const _RPR = isNaN(p.theoretical_PR) ? 0 : p.theoretical_PR / 100;

                //(TACATPAGAZE) Incidence Loss = RPR x Capacity x Monthly Incidence Irradiation - Monthly Incidence Production -> Check negative, if <0 then = 0
                const TA_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.m_TA_irr, p.m_TA_prod);
                const GA_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.m_GA_irr, p.m_GA_prod);
                const TPA_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.m_TPA_irr, p.m_TPA_prod);
                const CA_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.m_CA_irr, p.m_CA_prod);
                const ZE_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.m_ZE_irr, p.m_ZE_prod);
                // const overlap_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.overlap_irr, p.overlap_prod);
                // const ZE_loss = solarCalc.cal_inc_loss(_RPR, p.power, p.m_ZE_irr, p.m_ZE_prod) - overlap_loss;
                // const ZE_loss = _ZE_loss < 0 ? 0 : _ZE_loss;
                //DEO kWh = RPR x Capacity x Monthly Total Irradiation - Monthly TPA Loss - Monthly GA Loss
                const DEO = solarCalc.cal_new_DEO(_RPR, p.power, p.actual_irr, TPA_loss, GA_loss);

                //Unused = 0; EVN Curtailment = DEO - Monthly Production; if 100% FIT
                //Unused kWh = DEO - Monthly Production; EVN Curtailment =0; if SC
                const unused = p.contract_id === "SC" ? solarCalc.cal_unused_scheme(DEO, p.actual_prod) : 0;
                const curtailment = p.contract_id === "FIT" ? solarCalc.cal_unused_scheme(DEO, p.actual_prod) : 0;

                p.RPR = float_format(p.theoretical_PR, 2);
                p.TA_loss = float_format(TA_loss, 2);
                p.GA_loss = float_format(GA_loss, 2);
                p.TPA_loss = float_format(TPA_loss, 2);
                p.CA_loss = float_format(CA_loss, 2);
                p.ZE_loss = float_format(ZE_loss, 2);
                // p.overlap_loss = float_format(overlap_loss, 2);
                p.DEO = float_format(DEO, 2);
                p.unused = float_format(unused, 2);
                p.actual_prod = float_format(p.actual_prod, 2);
                p.actual_irr = float_format(parseFloat(p.actual_irr) / 6000, 2);
                p.curtailment = float_format(curtailment, 2);
                p.m_CA_irr = float_format(parseFloat(p.m_CA_irr) / 6000, 2);
                p.m_GA_irr = float_format(parseFloat(p.m_GA_irr) / 6000, 2);
                p.m_TA_irr = float_format(parseFloat(p.m_TA_irr) / 6000, 2);
                p.m_TPA_irr = float_format(parseFloat(p.m_TPA_irr) / 6000, 2);
                p.m_ZE_irr = float_format(parseFloat(p.m_ZE_irr) / 6000, 2);
                // p.overlap_irr = float_format(parseFloat(p.overlap_irr) / 6000, 2);
                p.m_CA_prod = float_format(p.m_CA_prod, 2);
                p.m_GA_prod = float_format(p.m_GA_prod, 2);
                p.m_TA_prod = float_format(p.m_TA_prod, 2);
                p.m_TPA_prod = float_format(p.m_TPA_prod, 2);
                p.m_ZE_prod = float_format(p.m_ZE_prod, 2);
                // p.overlap_prod = float_format(p.overlap_prod, 2);
                p.cod = moment(p.COD).format("YYYY-MM-DD");
                p.module_temp = float_format(p.module_temp, 2);
            });

            return month_data;
        } else { return []; }
    }

    const reload = useCallback(async () => {
        setLoading(true);

        let _startDate = FrozenMoment(startDate);
        _startDate = moment(_startDate).format("YYYY-MM");
        const monthly_uri = `/reports/monthly_ICRPR?monthyear=${_startDate}&selectedCountry=${selectedCountry}`;
        // const daily_uri = `/reports/sum_daily_ICRPR?monthyear=${_startDate}&selectedCountry=${selectedCountry}`;
        // const overlap_uri = `/reports/sum_overlap_dates_ICRPR?monthyear=${_startDate}&selectedCountry=${selectedCountry}`;

        await fetch_m_calc(monthly_uri);
        // await fetch_d_calc(daily_uri);
        // await fetch_o_calc(overlap_uri);
    }, [startDate, selectedCountry]);

    useEffect(() => {
        const timer = setTimeout(() => {
            reload();
        }, 500);
        return () => clearTimeout(timer);
    }, [reload]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (month_data && month_data.length > 0) {
                const processed_data = process_m_d_data(month_data);
                setData(processed_data);
                setLoading(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [month_data]);

    return (<>
        <div className="panel-body">
            <h3>INTERNATIONAL COORDINATION REPORT</h3>
            <Col md="4">
                <div className="month-selector">
                    Choose another Month: &nbsp;&nbsp;
                    <div style={{ display: "inline-block", width: '150px' }}>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="MMM yyyy"
                            disabled={loading}
                            showMonthYearPicker

                            showTwoColumnMonthYearPicker
                            maxDate={endDate}
                        />
                    </div>
                </div>
            </Col>
            <Col md="8" style={{ textAlign: "right" }}>
                <div className="month-selector">
                    <button
                        disabled={selectedCountry == "VN"}
                        className={"btn btn-primary"}
                        onClick={() => {
                            if (selectedCountry !== "VN") {
                                setSelectedCountry("VN");
                            }
                        }}
                    >VN</button>
                    <button
                        disabled={selectedCountry == "TH"}
                        className={"btn btn-primary"}
                        onClick={() => {
                            if (selectedCountry !== "TH") setSelectedCountry("TH");
                        }}
                    >TH</button>
                </div>
            </Col>
        </div>
        <div className="panel-body">
            <MaterialTable
                title={
                    <div>
                        <h5>
                            Monthly Report {" "}
                            <span
                                className="	glyphicon glyphicon-refresh"
                                onClick={() => reload()}
                            ></span>
                        </h5>
                    </div>
                }
                columns={icreport_cols}
                data={data}
                isLoading={loading}
                style={summarytable}
                options={{
                    /* showTitle: false, */
                    exportButton: true,
                    exportAllData: true,
                    grouping: true,
                    pageSize: 10,
                    pageSizeOptions: [10, 20, 30, 50, 100],
                    headerStyle: {
                        fontSize: "1rem",
                        textAlign: "center",
                    },
                    cellStyle: { textAlign: "center", },
                    fixedColumns: { left: 2, },
                    emptyRowsWhenPaging: false,
                    filtering: false,
                    exportFileName: `IC Report - ${moment(startDate).format("MMMM YYYY")}`,
                }}
            />
        </div></>
    );
};

export default React.memo(MonthlyLossesRPRReport);
